import React, { useContext } from "react";
import { snakeify } from "./../utils/caseConvert";
import { useAuth0 } from "./react-auth0-spa";
import axios from "axios";
import { parseCSVLine } from '../lib/helpers'
import { activeOrgHeader } from "../utils/activeOrg";

const baseUrlSec =
  process.env.REACT_APP_ENV !== "development" ? "https://" : "http://";
const baseURL = process.env.REACT_APP_BACKEND_ENDPOINT || "localhost:8000";
const ACAO =
  process.env.REACT_APP_ENV !== "development"
    ? process.env.REACT_APP_ENV === "staging"
      ? "https://mos-id-backend-staging.vectech.io"
      : "https://mos-id-backend.vectech.io"
    : "*";

const apiConfig = {
  mainAPI: {
    baseURL: baseUrlSec + baseURL,
  },
};

const organizationAPI = process.env.REACT_APP_ORGANIZATION_API;

const WebServerContext = React.createContext();
const useWebServer = () => useContext(WebServerContext);

const WebServerProvider = ({ children }) => {
  const { getTokenSilently } = useAuth0();

  const getToken = async (ignoreCache = false) => {
    try {
      return await getTokenSilently({ ignoreCache: ignoreCache });
    } catch (e) {
      try {
        return await getTokenSilently({ ignoreCache: ignoreCache });
      } catch (e) {
        console.error('fail to get token')
      }
    }
  };

  // Every internal API instance is built here - the main backend, the
  // organization microservice, and the websocket health ping - so the org header
  // is applied once, in one place. Read at call time rather than captured, so a
  // switch takes effect on the next request.
  //
  // Deliberately NOT applied to the two axios.get calls below: those go to
  // nominatim.openstreetmap.org, and an internal header has no business being
  // sent to a third party.
  const createApi = ({ baseURL, token }) =>
    axios.create({
      baseURL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": ACAO,
        ...activeOrgHeader(),
      },
    });

  const createTokenizedAPI = async ({ devPort }) => {
    const token = await getToken();
    return createApi({
      baseURL:
        devPort && process.env.REACT_APP_ENV === "development"
          ? String(apiConfig.mainAPI.baseURL).slice(0, -4) + devPort
          : apiConfig.mainAPI.baseURL,
      token,
    });
  };

  // `signal` (an AbortSignal) is opt-in per request and cancels it when aborted,
  // including while it is still waiting on the auth token: axios then rejects it
  // without sending it. axios has no default timeout, so a request that stalls in
  // the browser or on a dead connection otherwise never settles - no response
  // and no error. There is deliberately no global timeout: some callers (report
  // downloads, organization data) legitimately run long.
  const sendRequest =
    (method) =>
      async ({ url, data, params, devPort = null, isOrganizationAPI, signal }) => {
        const api = isOrganizationAPI
          ? await createOrganizationMsAPI()
          : await createTokenizedAPI({ devPort });
        const snakedParams = snakeify(params, true);
        const response = await api.request({
          url,
          method,
          data: snakeify(data) || {},
          params: snakedParams,
          signal,
        });
        return response;
      };

  // the organization service reads camelCase query params and body keys, and
  // snakeCase would also mangle the opaque pagination cursor
  const sendOrganizationRequest =
    (method) =>
      async ({ url, data, params }) => {
        const api = await createOrganizationMsAPI();
        return api.request({ url, method, params, data });
      };

  const getGPSLocationFromAddress = async (address) => {
    const resp = await axios.get(
      `https://nominatim.openstreetmap.org/search?q='+${address}&format=jsonv2&addressdetails=1`
    );
    return resp;
  };

  const getAddressFromGPSLocation = async (lat, lng) => {
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
      );
      return resp;
    } catch (err) {
      console.log(err);
    }
  };

  const wsReconnectPingServer = async ({ timeout = 0 }) => {
    const token = await getToken();
    const api = createApi({
      baseURL: getWebsocketBaseUrl(),
      token,
    });
    return api.get("/health", { timeout });
  };

  const getWebsocketUrl = () => {
    // const protocol = baseUrlSec === 'https://' ? 'wss://' : 'ws://';
    if (process.env.REACT_APP_ENV === "development") {
      return `${baseUrlSec}localhost:8889`;
    }
    return `${baseUrlSec}${baseURL}`;
  };

  const getWebsocketBaseUrl = () => {
    if (process.env.REACT_APP_ENV === "development") {
      return `${baseUrlSec}localhost:8889`;
    }
    return `${baseUrlSec}${baseURL}`;
  };

  const createOrganizationMsAPI = async () => {
    const token = await getToken();
    return createApi({
      baseURL: organizationAPI,
      token,
    });
  };

  const downloadOrganizationData = async ({ filters, isDownload = false }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data`,
      method: "post",
      data: filters || {},
      isDownload,
      responseType: "json",
    });
    return res;
  };

  const downloadOrganizationDataWithRawData = async ({
    filters,
    isDownload = false,
  }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    try {
      const response = await fetch(res?.data?.url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const csvText = await response.text();

      // Parse CSV data
      const rows = [];
      const lines = csvText.split("\n").filter((line) => line.trim() !== "");

      if (lines.length === 0) {
        return { data: [] };
      }

      // headers
      const headers = lines[0]
        .split(",")
        .map((header) => header.trim().replace(/"/g, ""));

      // Process each data row (skip header row)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const values = parseCSVLine(line);
        const rowObject = {};

        headers.forEach((header, index) => {
          rowObject[header] = values[index] || "";
        });

        rows.push(rowObject);
      }

      return { data: rows };
    } catch (error) {
      console.error("Error fetching JSON from S3:", error);
    }
  };

  const getOrganizationSummaryData = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/summary`,
      method: "post",
      data: filters || {},
    });
    return res?.data?.body;
  };

  const downloadOrganizationDataWithAbundance = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data/abundance`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  const downloadOrganizationDataWithWeekly = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data/weeklyreport`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    console.log("weeklyreport function", res);
    return res;
  };

  const downloadSpeciesLineChart = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data/linechart`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  const downloadMonthlyImaging = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data/monthlyImaging`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  const downloadWeeklyImaging = async ({ filters }) => {
    const api = await createOrganizationMsAPI();
    const res = await api.request({
      url: `/organization/data/weeklyImaging`,
      method: "post",
      data: filters || {},
      responseType: "json",
    });
    return res;
  };

  return (
    <WebServerContext.Provider
      value={{
        sendRequest,
        sendOrganizationRequest,
        getGPSLocationFromAddress,
        getAddressFromGPSLocation,
        getWebsocketUrl,
        wsReconnectPingServer,
        getToken,
        downloadOrganizationData,
        downloadOrganizationDataWithAbundance,
        downloadOrganizationDataWithRawData,
        downloadOrganizationDataWithWeekly,
        downloadSpeciesLineChart,
        getOrganizationSummaryData,
        downloadMonthlyImaging,
        downloadWeeklyImaging,
      }}
    >
      {children}
    </WebServerContext.Provider>
  );
};

export default WebServerProvider;
export { useWebServer };
